import { Link, useNavigate } from 'react-router-dom';
import type { GeoCoordinates } from '@repo/domain';
import type { TestId } from '@repo/test-id';
import { ClockArrowCcwIcon, Filters3Icon, PlanLocationIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import { Button, Badge, TextDotsAnimation } from '@repo/ui';
import { useHasActiveReservations } from '@repo/domain-service';

import { selectStatus, useAuthStore } from '@features/auth';
import AddressSelector from '@features/parkingSearch/components/address/AddressSelector';
import ParkStartInput from '@features/parkingSearch/components/parkStart/ParkStartInput';

import Logo from '@shared/components/Logo';
import { PRESET_DURATIONS } from '@shared/constants/parkingDurations';
import DurationPresetCard from '@shared/components/duration/DurationPresetCard';
import DurationCustomCard from '@shared/components/duration/DurationCustomCard';
import {
  selectParkDuration,
  selectSelectedFiltersCount,
  useParkingSearchStore,
} from '@shared/stores/useParkingSearchStore';
import HamburgerMenu from '@shared/components/HamburgerMenu';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  testId: TestId;
}

export default function DesktopBar({ testId }: Props) {
  const searchedAddress = useParkingSearchStore((state) => state.searchedAddress);
  const setSearchedAddress = useParkingSearchStore((state) => state.setSearchedAddress);
  const setParkDuration = useParkingSearchStore((state) => state.setParkDuration);
  const parkDuration = useParkingSearchStore(selectParkDuration);
  const toggleFilters = useParkingSearchStore((state) => state.toggleFilters);
  const selectedFiltersCount = useParkingSearchStore(selectSelectedFiltersCount);
  const navigate = useNavigate();
  const isLoggedIn = useAuthStore(selectStatus) !== 'unauthenticated';
  const { hasActiveReservations } = useHasActiveReservations(isLoggedIn);

  const handleDurationSelect = (minutes: number) => {
    setParkDuration(minutes);
    navigate(ROUTE_PATHS[RouteName.PARKING_SEARCH_RESULTS]);
  };

  const handleAddressChange = (value: string, coordinates?: GeoCoordinates) => {
    setSearchedAddress(value, coordinates);
  };
  const setIsTimeContractPickerModalOpen = useParkingSearchStore((state) => state.setIsTimeContractPickerModalOpen);

  const handleCustomDurationSelect = () => {
    setIsTimeContractPickerModalOpen(true);
  };

  return (
    <div className="bg-primary z-dropdown -mb-2 hidden w-full rounded-2xl shadow-2xl md:block">
      <div className="border-stroke-base grid grid-cols-3 items-center border-b p-5">
        <div className="flex items-center gap-4">
          {/* <Link to={ROUTE_PATHS[RouteName.PARKING_SEARCH_RESULTS]}>
            <div className="bg-brand-softer text-brand-strong flex items-center gap-1 rounded-lg p-2">
              <PlanLocationIcon size={24} /> Zaparkuj
            </div>
          </Link> */}
          {hasActiveReservations ? (
            <Link to={ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS]}>
              <div className="hover:text-brand-strong hover:bg-brand-softer bg-brand flex items-center gap-1 rounded-lg px-3 py-2 text-white">
                <PlanLocationIcon size={20} /> {tr('bottom_bar.active_session')} <TextDotsAnimation />
              </div>
            </Link>
          ) : (
            <Link to={ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS_HISTORY]}>
              <div className="text-body hover:text-brand-strong hover:bg-brand-softer flex items-center gap-1 rounded-lg p-2">
                <ClockArrowCcwIcon size={20} /> {tr('bottom_bar.history')}
              </div>
            </Link>
          )}
        </div>
        <div className="flex justify-center">
          <Link to={ROUTE_PATHS[RouteName.MAP]}>
            <Logo width={150} className="dark:text-white" />
          </Link>
        </div>
        <div className="flex justify-end">
          <HamburgerMenu />
        </div>
      </div>

      <div className="flex justify-center p-3">
        <div className="flex gap-6">
          <div className="flex flex-col gap-2">
            <div className="text-heading w-0 min-w-full truncate text-sm font-medium">
              {tr('parking_search:desktop_bar.address_label')}
            </div>
            <AddressSelector
              value={searchedAddress.label}
              onChange={handleAddressChange}
              size="sm"
              testId={testId.append('address')}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-heading text-sm font-medium">{tr('parking_search:desktop_bar.park_start_label')}</div>
            <ParkStartInput testId={testId.append('parkStart')} />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-heading text-sm font-medium">{tr('parking_search:park_duration.label')}</div>
            <div className="flex gap-2">
              {PRESET_DURATIONS.map((minutes) => (
                <DurationPresetCard
                  key={minutes}
                  minutes={minutes}
                  onClick={() => handleDurationSelect(minutes)}
                  testId={testId.append(`set${minutes}`)}
                  compact
                  color="white"
                />
              ))}
              <DurationCustomCard
                minutes={parkDuration}
                color="white"
                compact
                onClick={handleCustomDurationSelect}
                testId={testId.append('manage')}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-heading text-sm font-medium">{tr('parking_search:filter.title')}</div>
            <div className="relative flex items-center justify-center">
              <Button
                color="dark"
                size="sm"
                iconOnly
                leftIcon={Filters3Icon}
                onClick={toggleFilters}
                testId={testId.append('openFiltersButton')}
              />
              {selectedFiltersCount > 0 && (
                <Badge
                  color="ghost"
                  label={selectedFiltersCount.toString()}
                  iconOnly
                  circle
                  className="bg-danger absolute mt-5 mr-3 translate-x-full -translate-y-full border-0 text-white"
                  testId={testId.append('filtersCount')}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
